
import { useQuery } from '@apollo/client';
import { Space, Tabs } from 'antd';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { capitalize } from 'lodash';
import ItemListPagination from '../../shared/components/item-list/ItemListPagination';
import useSpinner from '../../hooks/layout/useSpinner';
import { COMM_REPORT, MEMBER_COMM_REPORT } from './CommissionReport.gql';
import { endOfDay, format } from "date-fns";
import { client } from '../../apollo-clients';
import CommissionReportFilter from './CommissionReportFilter';
import moment from 'moment';
import TabPane from 'antd/lib/tabs/TabPane';
import TableNumberFormatter from '../../shared/components/table-number-formatter/TableNumberFormatter';

const CommissionReport = () => {
    const intl = useIntl();
    const { setLoading } = useSpinner();
    const [pageNo, setPageNo] = useState<number>(1);
    const [memberPageNo, setMemberPageNo] = useState<number>(1);
    const pageSize = process.env.REACT_APP_PAGE_SIZE ? +process.env.REACT_APP_PAGE_SIZE : 10;
    const [total, setTotal] = useState<number>(0);
    const [memberTotal, setMemberTotal] = useState<number>(0);
    const [reportData, setReportData] = useState<any>();
    const [memberReportData, setMemberReportData] = useState<any>();
    const [filters, setFilters] = useState({
        userName: '', status: 'PENDING', currency: '',
        startDate: (new Date(moment().subtract(1, "months").toString())), endDate: endOfDay(new Date())
    });
    const { data, loading, refetch } = useQuery(COMM_REPORT, {
        variables: { pageNo, pageSize, commissionReportFilterInput: filters },
        client, fetchPolicy: 'cache-and-network'
    });
    const { data: memberCommData, loading: memberCommLoading, refetch: memberCommRefetch } = useQuery(MEMBER_COMM_REPORT, {
        variables: { pageNo, pageSize, commissionReportFilterInput: filters },
        client, fetchPolicy: 'cache-and-network'
    });
    const [currentTab, setCurrentTab] = useState<string>("1");

    useEffect(() => {
        if (currentTab === '1') {
            refetch();
        } else {
            memberCommRefetch();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, currentTab]);

    useEffect(() => {
        if (loading || memberCommLoading)
            setLoading(true);
        else
            setLoading(false); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, memberCommLoading]);

    useEffect(() => {
        if (memberCommData && memberCommData.memberCommissionReport) {
            setMemberTotal(memberCommData?.memberCommissionReport?.count ?? 0);

            if (memberCommData.memberCommissionReport?.commissionReport && memberCommData.memberCommissionReport?.commissionReport?.length > 0) {
                let reportData = memberCommData.memberCommissionReport?.commissionReport.map((val: any) => {
                    return {
                        ...val,
                        key: val.id
                    };
                })

                setMemberReportData(reportData);
            }
        }
    }, [memberCommData])

    useEffect(() => {
        if (data && data.commissionReport) {
            setTotal(data?.commissionReport?.count ?? 0);

            if (data.commissionReport?.commissionReport && data.commissionReport?.commissionReport?.length > 0) {
                let reportData = data.commissionReport?.commissionReport.map((val: any) => {
                    return {
                        ...val,
                        key: val.id
                    };
                })

                setReportData(reportData);
            }
        }
    }, [data])

    const onChangeTab = (activeKey: string) => {
        setCurrentTab(activeKey);
    };


    const columns: any = [
        {
            title: intl.formatMessage({ id: 'DASHBOARD_CR_YM' }),
            dataIndex: 'commDate',
            render: (text: any) => {
                const createdAt = text ? format(new Date(text), "yyyyMM") : '-';
                return (
                    <Space size="middle">
                        {createdAt}
                    </Space>
                )
            }
        }, {
            title: intl.formatMessage({ id: 'DASHBOARD_CR_ID' }),
            dataIndex: 'aff',
            render: (aff: any) => {
                return (
                    <Space size="middle">
                        {aff && aff.userName ? aff?.userName : '-'}
                    </Space>
                )
            }
        }, {
            title: intl.formatMessage({ id: 'DASHBOARD_CR_REG_DATE' }),
            dataIndex: 'aff',
            render: (aff: any) => {
                const createdAt = aff && aff.createdAt ? format(new Date(aff.createdAt), "dd/MM/yyyy HH:mm") : '-';
                return (
                    <Space size="middle">
                        {createdAt}
                    </Space>
                )
            }
        }, {
            title: intl.formatMessage({ id: 'DASHBOARD_CR_TOTALREG' }),
            dataIndex: 'totalRegistered',
            render: (text: any) => {
                return (
                    <Space size="middle">
                        {text ?? '0'}
                    </Space>
                )
            }
        }, {
            title: intl.formatMessage({ id: 'DASHBOARD_CR_ACTIVE_PLAYER' }),
            dataIndex: 'activePlayer',
            render: (text: any) => {
                return (
                    <Space size="middle">
                        {text ?? '0'}
                    </Space>
                )
            }
        }, {
            title: intl.formatMessage({ id: 'DASHBOARD_CR_TOTAL_WIN_LOSS' }),
            dataIndex: 'totalWinLoss',
            render: (text: any) => {
                return (
                    <TableNumberFormatter number={text} decimalScale={2} />
                )
            },
        }, {
            title: intl.formatMessage({ id: 'DASHBOARD_CR_PLATFORM_FEE' }),
            dataIndex: 'platformFee',
            render: (text: any) => {
                return (
                    <TableNumberFormatter number={text} decimalScale={2} />
                )
            },
        }, {
            title: intl.formatMessage({ id: 'DASHBOARD_CR_BONUS_FEE' }),
            dataIndex: 'bonusFee',
            render: (text: any) => {
                return (
                    <TableNumberFormatter number={text} decimalScale={2} />
                )
            },
        }, {
            title: intl.formatMessage({ id: 'DASHBOARD_CR_REBATE_FEE' }),
            dataIndex: 'rebateFee',
            render: (text: any) => {
                return (
                    <TableNumberFormatter number={text} decimalScale={2} />
                )
            },
        }, {
            title: intl.formatMessage({ id: 'DASHBOARD_CR_COMMRATE' }),
            dataIndex: 'commRate',
            render: (text: any) => {
                return (
                    <TableNumberFormatter number={text} decimalScale={2} />
                )
            },
        }, {
            title: intl.formatMessage({ id: 'DASHBOARD_GCR_AMOUNT' }),
            dataIndex: 'grossCommission',
            render: (text: any) => {
                return (
                    <TableNumberFormatter number={text} decimalScale={2} suffix={'%'} />
                )
            },
        }, {
            title: intl.formatMessage({ id: 'DASHBOARD_CR_AMOUNT' }),
            dataIndex: 'amount',
            render: (text: any) => {
                return (
                    <TableNumberFormatter number={text} decimalScale={2} />
                )
            },
        }, {
            title: intl.formatMessage({ id: 'DASHBOARD_CR_STATUS' }),
            dataIndex: 'status',
            render: (text: any) => {
                return (
                    <Space size="middle">
                        {capitalize(text) ?? '0'}
                    </Space>
                )
            }
        }, {
            title: intl.formatMessage({ id: 'DASHBOARD_PAIDAT_AMOUNT' }),
            dataIndex: 'updatedAt',
            render: (text: any) => {
                const updatedAt = text ? format(new Date(text), "dd/MM/yyyy HH:mm") : '-';
                return (
                    <Space size="middle">
                        {updatedAt}
                    </Space>
                )
            }
        },
    ]

    return (
        <div className="member-list-container">
            <CommissionReportFilter setFilters={setFilters} setPageNo={currentTab === '1' ? setPageNo : setMemberPageNo} filters={filters} />
            <Tabs destroyInactiveTabPane defaultActiveKey="1" activeKey={currentTab} onChange={onChangeTab}>
                <TabPane tab={<FormattedMessage id={'ACCOUNT'} />} key="1" >
                    <ItemListPagination pageNo={pageNo} pageSize={pageSize} setPageNo={setPageNo} columns={columns} data={reportData || []}
                        total={total} />
                </TabPane>
                <TabPane tab={<FormattedMessage id={'DOWNLINE'} />} key="2" >
                    <ItemListPagination pageNo={memberPageNo} pageSize={pageSize} setPageNo={setMemberPageNo} columns={columns} data={memberReportData || []}
                        total={memberTotal} />
                </TabPane>
            </Tabs>
        </div>
    )
}

export default CommissionReport;